// Generic Supabase Realtime subscription hook.
// One channel per hook instance. 50ms event batching. Channel state to Sentry.
//
// Usage:
//   useRealtimeSubscription({
//     table:    'developments',
//     event:    '*',                        // '*' | 'INSERT' | 'UPDATE' | 'DELETE'
//     filter:   'report_id=eq.abc-123',     // optional PostgREST filter
//     onPayload: (events) => { ... },       // called once per batch; events is Array (length >= 1)
//     enabled:  true,                       // gate the subscription on/off
//   });
//
// Returns: { status } — 'CONNECTING' | 'SUBSCRIBED' | 'CHANNEL_ERROR' | 'TIMED_OUT' | 'CLOSED'
//
// onPayload always receives an array. Single events arrive as a 1-element array;
// bursts within 50ms arrive as a single N-element array. Consumers that key on
// row id should iterate the array.

import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../supabase';
import { browserDay, firstTimeToday } from '../utils/sentryGate';

const BATCH_WINDOW_MS = 50;

export function useRealtimeSubscription({
  table,
  event = '*',
  filter,
  onPayload,
  enabled = true,
}) {
  const [status, setStatus] = useState('CONNECTING');

  // Stable callback ref — parent re-renders shouldn't tear down the channel.
  const onPayloadRef = useRef(onPayload);
  useEffect(() => { onPayloadRef.current = onPayload; }, [onPayload]);

  useEffect(() => {
    if (!enabled || !table) {
      setStatus('CLOSED');
      return undefined;
    }

    setStatus('CONNECTING');

    // Random suffix avoids name collisions when the same hook fires twice
    // (e.g. StrictMode double-invoke in dev, or two consumers on same table).
    const channelName = `realtime-${table}-${Math.random().toString(36).slice(2, 10)}`;

    let pending = [];
    let timer = null;

    const flush = () => {
      timer = null;
      if (pending.length === 0) return;
      const batch = pending;
      pending = [];
      try {
        onPayloadRef.current?.(batch);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { realtime_table: table, source: 'useRealtimeSubscription.flush' },
        });
      }
    };

    const handleEvent = (payload) => {
      pending.push(payload);
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, BATCH_WINDOW_MS);
    };

    const eventConfig = { event, schema: 'public', table };
    if (filter) eventConfig.filter = filter;

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', eventConfig, handleEvent)
      .subscribe((subStatus, err) => {
        setStatus(subStatus);

        if (subStatus === 'SUBSCRIBED') {
          Sentry.addBreadcrumb({
            category: 'realtime',
            level: 'info',
            message: `${table}: SUBSCRIBED`,
          });
        } else if (subStatus === 'CLOSED') {
          // Normal teardown — keep as breadcrumb so debugging context survives,
          // but don't fire a Sentry event per unmount.
          Sentry.addBreadcrumb({
            category: 'realtime',
            message: `realtime.${table}.closed`,
            level: 'info',
            data: {
              realtime_table: table,
              realtime_status: subStatus,
              error: err?.message,
            },
          });
        } else if (subStatus === 'CHANNEL_ERROR' || subStatus === 'TIMED_OUT') {
          // SYS-11: one Sentry event per table per browser per day; repeats are
          // breadcrumbs. The message string is unchanged so issues keep grouping.
          const message = `realtime.${table}.${subStatus.toLowerCase()}`;
          if (firstTimeToday(message, browserDay())) {
            Sentry.captureMessage(message, {
              level: 'warning',
              tags: { realtime_table: table, realtime_status: subStatus },
              extra: { error: err?.message },
            });
          } else {
            Sentry.addBreadcrumb({
              category: 'realtime',
              level: 'warning',
              message,
              data: { realtime_table: table, realtime_status: subStatus, error: err?.message },
            });
          }
        }
      });

    return () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      // Drop any pending events — consumer is unmounting and shouldn't be called after cleanup.
      pending = [];
      supabase.removeChannel(channel);
    };
  }, [table, event, filter, enabled]);

  return { status };
}
